import React, { useState, useEffect, useRef } from 'react';
import { Filter, X, ChevronDown, Search, Calendar, Users, MapPin, Briefcase, Tag, Loader2 } from 'lucide-react';
import { useMmsLookups } from '../../../hooks/useMmsLookups';
import CustomDatePicker from '../../common/CustomDatePicker';

/**
 * Advanced Job Card Filters Component
 * Provides comprehensive filtering capabilities for job cards:
 * - Date range selection
 * - Multi-select application types
 * - OM status filtering
 * - Priority levels
 * - Stations/depots
 * - Assigned workers
 * - Quick filter presets
 */
const AdvancedJobCardFilters = ({ filters, onFiltersChange, workers = [], onApply, onReset }) => {
  const { lookups, loading: lookupsLoading } = useMmsLookups();
  const [isExpanded, setIsExpanded] = useState(false);
  const [localFilters, setLocalFilters] = useState(filters);
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);
  const [isApplying, setIsApplying] = useState(false);

  // Helper functions to get display text for selected options
  const getStatusText = () => {
    if (!localFilters.status) return null;
    const status = (lookups.om_statuses || []).find(s => s.OM_STATUS === localFilters.status);
    return status?.status_name || localFilters.status;
  };

  const getPriorityText = () => {
    if (!localFilters.priority) return null;
    const priority = (lookups.priorities || []).find(p => p.PRIORITY_RANK == localFilters.priority);
    return priority?.PRIORITY || null;
  };

  const getStationText = () => {
    if (!localFilters.station_no) return null;
    const station = (lookups.stations || []).find(s => s.STATION_NO == localFilters.station_no);
    return station?.NAME || null;
  };

  const getJobTypeText = () => {
    if (!localFilters.mactivity_no) return null;
    const activity = (lookups.maintenance_activities || []).find(a => a.MACTIVITY_NO == localFilters.mactivity_no);
    return activity?.DESCRIPTION || activity?.NAME || null;
  };

  const getSourceTypeText = () => {
    if (!localFilters.source_type) return null;
    const sourceType = (lookups.source_types || []).find(st => st.SOURCE_TYPE_ID == localFilters.source_type);
    return sourceType?.CODE || sourceType?.DESCRIPTION || null;
  };

  // State for multi-select dropdowns
  const [showApplicationTypes, setShowApplicationTypes] = useState(false);
  const [showStations, setShowStations] = useState(false);
  const [showWorkers, setShowWorkers] = useState(false);
  const [showJobDescriptions, setShowJobDescriptions] = useState(false);
  const [applicationTypeSearch, setApplicationTypeSearch] = useState('');
  const [stationSearch, setStationSearch] = useState('');
  const [workerSearch, setWorkerSearch] = useState('');
  const [jobDescriptionSearch, setJobDescriptionSearch] = useState('');
  const [activeQuickFilter, setActiveQuickFilter] = useState(null);
  
  // Refs for click-outside detection
  const workersRef = useRef(null);
  const stationsRef = useRef(null);
  const applicationTypesRef = useRef(null);
  const jobDescriptionsRef = useRef(null);
  
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  // Count active filters
  useEffect(() => {
    let count = 0;
    if (localFilters.status) count++;
    if (localFilters.priority) count++;
    if (localFilters.assigned_to) count++;
    if (localFilters.station_no) count++;
    if (localFilters.application_types && localFilters.application_types.length > 0) count++;
    if (localFilters.source_type) count++;
    setActiveFiltersCount(count);
  }, [localFilters]);

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (workersRef.current && !workersRef.current.contains(event.target)) {
        setShowWorkers(false);
      }
      if (stationsRef.current && !stationsRef.current.contains(event.target)) {
        setShowStations(false);
      }
      if (applicationTypesRef.current && !applicationTypesRef.current.contains(event.target)) {
        setShowApplicationTypes(false);
      }
      if (jobDescriptionsRef.current && !jobDescriptionsRef.current.contains(event.target)) {
        setShowJobDescriptions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Convert dd/MM/yyyy to yyyy-MM-dd
  const convertDateFormat = (dateString) => {
    if (!dateString) return '';
    // Check if already in yyyy-MM-dd format
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) return dateString;
    // Convert from dd/MM/yyyy to yyyy-MM-dd
    if (dateString.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      const [day, month, year] = dateString.split('/');
      return `${year}-${month}-${day}`;
    }
    return dateString;
  };

  const handleLocalChange = (field, value) => {
    // Convert date formats for date fields
    if (field === 'start_date' || field === 'end_date') {
      value = convertDateFormat(value);
    }
    setLocalFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleApplicationTypeToggle = (typeId) => {
    const current = localFilters.application_types || [];
    const updated = current.includes(typeId)
      ? current.filter(id => id !== typeId)
      : [...current, typeId];
    handleLocalChange('application_types', updated);
  };

  const handleApply = async () => {
    setIsApplying(true);
    onFiltersChange(localFilters);
    if (onApply) {
      try {
        await onApply();
      } finally {
        // Add a small delay to show the loading state
        setTimeout(() => setIsApplying(false), 300);
      }
    } else {
      setTimeout(() => setIsApplying(false), 300);
    }
  };

  const handleReset = () => {
    const resetFilters = {
      start_date: (() => {
        const date = new Date();
        date.setDate(date.getDate() - 30);
        return date.toISOString().split('T')[0];
      })(),
      end_date: new Date().toISOString().split('T')[0],
      status: '',
      priority: '',
      assigned_to: '',
      station_no: '',
      application_types: [],
      source_type: '',
      mactivity_no: ''
    };
    setLocalFilters(resetFilters);
    onFiltersChange(resetFilters);
    setActiveQuickFilter(null);
    if (onReset) onReset();
  };

  // Quick filter presets
  const applyQuickFilter = (preset) => {
    const today = new Date();
    let newFilters = { ...localFilters };

    switch (preset) {
      case 'today':
        newFilters.start_date = today.toISOString().split('T')[0];
        newFilters.end_date = today.toISOString().split('T')[0];
        setActiveQuickFilter('today');
        break;
      case 'week':
        const weekAgo = new Date(today);
        weekAgo.setDate(today.getDate() - 7);
        newFilters.start_date = weekAgo.toISOString().split('T')[0];
        newFilters.end_date = today.toISOString().split('T')[0];
        setActiveQuickFilter('week');
        break;
      case 'month':
        const monthAgo = new Date(today);
        monthAgo.setDate(today.getDate() - 30);
        newFilters.start_date = monthAgo.toISOString().split('T')[0];
        newFilters.end_date = today.toISOString().split('T')[0];
        setActiveQuickFilter('month');
        break;
      case 'pending':
        newFilters.status = '0';
        setActiveQuickFilter('pending');
        break;
      case 'assigned':
        newFilters.status = '1';
        setActiveQuickFilter('assigned');
        break;
      case 'in-progress':
        newFilters.status = '3';
        setActiveQuickFilter('in-progress');
        break;
      case 'completed':
        newFilters.status = '6';
        setActiveQuickFilter('completed');
        break;
      case 'urgent':
        newFilters.priority = '1';
        setActiveQuickFilter('urgent');
        break;
      case 'emergency':
        newFilters.priority = '0';
        setActiveQuickFilter('emergency');
        break;
      default:
        break;
    }

    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
    if (onApply) onApply();
  };

  const filteredApplicationTypes = (lookups.application_types || []).filter(type =>
    type.TYPE_NAME?.toLowerCase().includes(applicationTypeSearch.toLowerCase()) ||
    type.DESCRIPTION?.toLowerCase().includes(applicationTypeSearch.toLowerCase())
  );

  const filteredStations = (lookups.stations || []).filter(station =>
    station.NAME?.toLowerCase().includes(stationSearch.toLowerCase()) ||
    station.CODE?.toLowerCase().includes(stationSearch.toLowerCase())
  );

  const filteredWorkers = (workers || []).filter(worker =>
    worker.worker_name?.toLowerCase().includes(workerSearch.toLowerCase())
  );

  const filteredJobDescriptions = (lookups.maintenance_activities || []).filter(activity =>
    activity.DESCRIPTION?.toLowerCase().includes(jobDescriptionSearch.toLowerCase()) ||
    activity.NAME?.toLowerCase().includes(jobDescriptionSearch.toLowerCase())
  );

  return (
    <div className="bg-white rounded-xl shadow-md border border-slate-200">
      {/* Header with Quick Filters - Soft, gentle gradient */}
      <div className="px-4 py-3 bg-gradient-to-br from-blue-50 via-sky-50 to-indigo-50 border-b border-blue-100 rounded-t-xl">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2 flex-wrap gap-2">
            <Filter className="h-5 w-5 text-[#0086D4]" />
            <h3 className="text-sm font-semibold text-slate-700">Advanced Filters</h3>
            {/* Always show date range */}
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200">
              📅 {localFilters.start_date ? new Date(localFilters.start_date).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' }) : 'Start'} - {localFilters.end_date ? new Date(localFilters.end_date).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }) : 'End'}
            </span>
            {/* Show individual active filters */}
            {localFilters.status && getStatusText() && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 border border-emerald-200">
                {getStatusText()}
              </span>
            )}
            {localFilters.priority && getPriorityText() && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700 border border-orange-200">
                {getPriorityText()}
              </span>
            )}
            {localFilters.assigned_to && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-700 border border-cyan-200">
                👤 {localFilters.assigned_to}
              </span>
            )}
            {localFilters.station_no && getStationText() && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 border border-purple-200">
                📍 {getStationText()}
              </span>
            )}
            {localFilters.mactivity_no && getJobTypeText() && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 border border-emerald-200">
                {getJobTypeText()}
              </span>
            )}
            {localFilters.application_types && localFilters.application_types.length > 0 && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200">
                🔧 {localFilters.application_types.length} App Type{localFilters.application_types.length > 1 ? 's' : ''}
              </span>
            )}
            {localFilters.source_type && getSourceTypeText() && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-700 border border-violet-200">
                🔌 {getSourceTypeText()}
              </span>
            )}
          </div>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center space-x-1 px-3 py-1.5 text-sm text-slate-600 hover:bg-white/80 rounded-lg transition-all font-medium"
          >
            <span>{isExpanded ? 'Hide Advanced Filters' : 'Show Advanced Filters'}</span>
            <ChevronDown className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* Quick Filter Buttons */}
        <div className="flex flex-wrap gap-2">
          {/* Date Presets */}
          <div className="flex items-center space-x-1.5">
            <span className="text-xs text-slate-700 font-semibold">Date:</span>
            <button 
              onClick={() => applyQuickFilter('today')} 
              className={`px-2.5 py-1 text-xs rounded-md transition-all ${
                activeQuickFilter === 'today' 
                  ? 'bg-[#0086D4] text-white font-semibold shadow-sm' 
                  : 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200'
              }`}
            >
              Today
            </button>
            <button 
              onClick={() => applyQuickFilter('week')} 
              className={`px-2.5 py-1 text-xs rounded-md transition-all ${
                activeQuickFilter === 'week' 
                  ? 'bg-[#0086D4] text-white font-semibold shadow-sm' 
                  : 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200'
              }`}
            >
              7 Days
            </button>
            <button 
              onClick={() => applyQuickFilter('month')} 
              className={`px-2.5 py-1 text-xs rounded-md transition-all ${
                activeQuickFilter === 'month' 
                  ? 'bg-[#0086D4] text-white font-semibold shadow-sm' 
                  : 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200'
              }`}
            >
              30 Days
            </button>
          </div>

          <div className="h-5 w-px bg-slate-300" />

          {/* Status Presets */}
          <div className="flex items-center space-x-1.5">
            <span className="text-xs text-slate-700 font-semibold">Status:</span>
            <button 
              onClick={() => applyQuickFilter('pending')} 
              className={`px-2.5 py-1 text-xs rounded-md transition-all ${
                activeQuickFilter === 'pending' 
                  ? 'bg-amber-500 text-white font-semibold shadow-sm' 
                  : 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200'
              }`}
            >
              Pending
            </button>
            <button 
              onClick={() => applyQuickFilter('assigned')} 
              className={`px-2.5 py-1 text-xs rounded-md transition-all ${
                activeQuickFilter === 'assigned' 
                  ? 'bg-cyan-500 text-white font-semibold shadow-sm' 
                  : 'bg-cyan-50 text-cyan-700 hover:bg-cyan-100 border border-cyan-200'
              }`}
            >
              Assigned
            </button>
            <button 
              onClick={() => applyQuickFilter('in-progress')} 
              className={`px-2.5 py-1 text-xs rounded-md transition-all ${
                activeQuickFilter === 'in-progress' 
                  ? 'bg-purple-500 text-white font-semibold shadow-sm' 
                  : 'bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200'
              }`}
            >
              In Progress
            </button>
            <button 
              onClick={() => applyQuickFilter('completed')} 
              className={`px-2.5 py-1 text-xs rounded-md transition-all ${
                activeQuickFilter === 'completed' 
                  ? 'bg-emerald-500 text-white font-semibold shadow-sm' 
                  : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200'
              }`}
            >
              Completed
            </button>
          </div>

          <div className="h-5 w-px bg-slate-300" />

          {/* Priority Presets */}
          <div className="flex items-center space-x-1.5">
            <span className="text-xs text-slate-700 font-semibold">Priority:</span>
            <button 
              onClick={() => applyQuickFilter('emergency')} 
              className={`px-2.5 py-1 text-xs rounded-md transition-all ${
                activeQuickFilter === 'emergency' 
                  ? 'bg-rose-500 text-white font-semibold shadow-sm' 
                  : 'bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-200'
              }`}
            >
              Emergency
            </button>
            <button 
              onClick={() => applyQuickFilter('urgent')} 
              className={`px-2.5 py-1 text-xs rounded-md transition-all ${
                activeQuickFilter === 'urgent' 
                  ? 'bg-orange-500 text-white font-semibold shadow-sm' 
                  : 'bg-orange-50 text-orange-700 hover:bg-orange-100 border border-orange-200'
              }`}
            >
              Urgent
            </button>
          </div>
        </div>
      </div>

      {/* Expanded Filters - White/Light background for strong contrast */}
      {isExpanded && (
        <div className="px-4 py-4 bg-gray-50 rounded-b-xl">{/* Clean light gray body */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* OM Status */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-2 flex items-center">
                <Tag className="h-4 w-4 mr-1 text-blue-500" />
                Job Status
              </label>
              <select
                value={localFilters.status || ''}
                onChange={(e) => handleLocalChange('status', e.target.value)}
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent shadow-sm hover:border-slate-300 transition-colors"
                disabled={lookupsLoading}
              >
                <option value="">All Statuses</option>
                {(lookups.om_statuses || []).map(status => (
                  <option key={status.OM_STATUS} value={status.OM_STATUS}>
                    {status.status_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Priority */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-2 flex items-center">
                <Briefcase className="h-4 w-4 mr-1 text-indigo-500" />
                Priority Level
              </label>
              <select
                value={localFilters.priority || ''}
                onChange={(e) => handleLocalChange('priority', e.target.value)}
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent shadow-sm hover:border-slate-300 transition-colors"
                disabled={lookupsLoading}
              >
                <option value="">All Priorities</option>
                {(lookups.priorities || []).map(priority => (
                  <option key={priority.PRIORITY_RANK} value={priority.PRIORITY_RANK}>
                    {priority.PRIORITY}
                  </option>
                ))}
              </select>
            </div>

            {/* Source Type */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-2 flex items-center">
                <Tag className="h-4 w-4 mr-1 text-violet-500" />
                Source Type
              </label>
              <select
                value={localFilters.source_type || ''}
                onChange={(e) => handleLocalChange('source_type', e.target.value)}
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent shadow-sm hover:border-slate-300 transition-colors"
                disabled={lookupsLoading}
              >
                <option value="">All Sources</option>
                {(lookups.source_types || []).filter(st => st.ACTIVE === 1).map(sourceType => (
                  <option key={sourceType.SOURCE_TYPE_ID} value={sourceType.SOURCE_TYPE_ID}>
                    {sourceType.DESCRIPTION}
                  </option>
                ))}
              </select>
            </div>

            {/* Assigned Worker */}
            <div className="relative" ref={workersRef}>
              <label className="block text-xs font-semibold text-slate-700 mb-2 flex items-center">
                <Users className="h-4 w-4 mr-1 text-cyan-500" />
                Assigned To
              </label>
              <button
                onClick={() => setShowWorkers(!showWorkers)}
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-left focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent shadow-sm hover:border-slate-300 transition-colors flex items-center justify-between"
                disabled={lookupsLoading}
              >
                <span className="text-slate-700">
                  {localFilters.assigned_to || 'All Workers'}
                </span>
                <ChevronDown className="h-4 w-4 text-slate-400" />
              </button>
              
              {showWorkers && (
                <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                  <div className="p-2 border-b border-slate-200">
                    <div className="relative">
                      <Search className="absolute left-2 top-2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={workerSearch}
                        onChange={(e) => setWorkerSearch(e.target.value)}
                        placeholder="Search workers..."
                        className="w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div className="py-1">
                    <button
                      onClick={() => {
                        handleLocalChange('assigned_to', '');
                        setShowWorkers(false);
                      }}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 text-gray-700"
                    >
                      All Workers
                    </button>
                    {filteredWorkers.map((worker, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          handleLocalChange('assigned_to', worker.worker_name);
                          setShowWorkers(false);
                        }}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 text-gray-700"
                      >
                        {worker.worker_name} <span className="text-xs text-gray-500">({worker.active_jobs} active)</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Maintenance Activity / Job Description */}
            <div className="relative" ref={jobDescriptionsRef}>
              <label className="block text-xs font-semibold text-slate-700 mb-2 flex items-center">
                <Briefcase className="h-4 w-4 mr-1 text-emerald-500" />
                Job Description
              </label>
              <button
                onClick={() => setShowJobDescriptions(!showJobDescriptions)}
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-left focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent shadow-sm hover:border-slate-300 transition-colors flex items-center justify-between"
                disabled={lookupsLoading}
              >
                <span className="text-gray-700">
                  {getJobTypeText() || 'All Job Types'}
                </span>
                <ChevronDown className="h-4 w-4 text-gray-400" />
              </button>
              
              {showJobDescriptions && (
                <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                  <div className="p-2 border-b border-gray-200">
                    <div className="relative">
                      <Search className="absolute left-2 top-2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={jobDescriptionSearch}
                        onChange={(e) => setJobDescriptionSearch(e.target.value)}
                        placeholder="Search job types..."
                        className="w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div className="py-1">
                    <button
                      onClick={() => {
                        handleLocalChange('mactivity_no', '');
                        setShowJobDescriptions(false);
                      }}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 text-gray-700"
                    >
                      All Job Types
                    </button>
                    {filteredJobDescriptions.map((activity) => (
                      <button
                        key={activity.MACTIVITY_NO}
                        onClick={() => {
                          handleLocalChange('mactivity_no', activity.MACTIVITY_NO.toString());
                          setShowJobDescriptions(false);
                        }}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 text-gray-700"
                      >
                        {activity.DESCRIPTION}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Custom flex row: Station/Depot, From Date, To Date, Application Types */}
            <div className="col-span-1 md:col-span-2 lg:col-span-3 flex flex-col md:flex-row gap-4">
              {/* Station/Depot - 20% */}
              <div className="relative md:w-[20%]" ref={stationsRef}>
                <label className="block text-xs font-semibold text-slate-700 mb-2 flex items-center">
                  <MapPin className="h-4 w-4 mr-1 text-purple-500" />
                  Station/Depot
                </label>
                <button
                  onClick={() => setShowStations(!showStations)}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-left focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent shadow-sm hover:border-slate-300 transition-colors flex items-center justify-between"
                  disabled={lookupsLoading}
                >
                  <span className="text-slate-700">
                    {getStationText() || 'All Stations'}
                  </span>
                  <ChevronDown className="h-4 w-4 text-slate-400" />
                </button>
                
                {showStations && (
                  <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                    <div className="p-2 border-b border-gray-200">
                      <div className="relative">
                        <Search className="absolute left-2 top-2 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          value={stationSearch}
                          onChange={(e) => setStationSearch(e.target.value)}
                          placeholder="Search stations..."
                          className="w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    <div className="py-1">
                      <button
                        onClick={() => {
                          handleLocalChange('station_no', '');
                          setShowStations(false);
                        }}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 text-gray-700"
                      >
                        All Stations
                      </button>
                      {filteredStations.map(station => (
                        <button
                          key={station.STATION_NO}
                          onClick={() => {
                            handleLocalChange('station_no', station.STATION_NO.toString());
                            setShowStations(false);
                          }}
                          className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 text-gray-700"
                        >
                          {station.NAME} {station.CODE && <span className="text-xs text-gray-500">({station.CODE})</span>}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* From Date - 20% */}
              <div className="md:w-[20%]">
                <label className="block text-xs font-semibold text-slate-700 mb-2 flex items-center">
                  <Calendar className="h-4 w-4 mr-1 text-teal-500" />
                  From Date
                </label>
                <CustomDatePicker
                  value={localFilters.start_date}
                  onChange={(value) => handleLocalChange('start_date', value)}
                  placeholder="Start Date"
                  className="w-full"
                />
              </div>

              {/* To Date - 20% */}
              <div className="md:w-[20%]">
                <label className="block text-xs font-semibold text-slate-700 mb-2 flex items-center">
                  <Calendar className="h-4 w-4 mr-1 text-teal-500" />
                  To Date
                </label>
                <CustomDatePicker
                  value={localFilters.end_date}
                  onChange={(value) => handleLocalChange('end_date', value)}
                  placeholder="End Date"
                  className="w-full"
                />
              </div>

              {/* Application Types - 40% */}
              <div className="relative md:w-[40%]" ref={applicationTypesRef}>
              <label className="block text-xs font-semibold text-slate-700 mb-2 flex items-center">
                <Briefcase className="h-4 w-4 mr-1 text-amber-500" />
                Application Types {localFilters.application_types?.length > 0 && (
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100/80 text-amber-700 border border-amber-200/50">
                    {localFilters.application_types.length} selected
                  </span>
                )}
              </label>
              <button
                onClick={() => setShowApplicationTypes(!showApplicationTypes)}
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-left focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent shadow-sm hover:border-slate-300 transition-colors flex items-center justify-between"
                disabled={lookupsLoading}
              >
                <span className="text-slate-700 truncate">
                  {localFilters.application_types?.length > 0
                    ? `${localFilters.application_types.length} type(s) selected`
                    : 'All Application Types'}
                </span>
                <ChevronDown className="h-4 w-4 text-slate-400 flex-shrink-0" />
              </button>
              
              {showApplicationTypes && (
                <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-96 overflow-auto">
                  <div className="p-2 border-b border-slate-200 sticky top-0 bg-white">
                    <div className="relative mb-2">
                      <Search className="absolute left-2 top-2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={applicationTypeSearch}
                        onChange={(e) => setApplicationTypeSearch(e.target.value)}
                        placeholder="Search application types..."
                        className="w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleLocalChange('application_types', [])}
                        className="flex-1 px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                      >
                        Clear All
                      </button>
                      <button
                        onClick={() => handleLocalChange('application_types', filteredApplicationTypes.map(t => t.APPLICATION_TYPE_ID))}
                        className="flex-1 px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                      >
                        Select All
                      </button>
                    </div>
                  </div>
                  <div className="py-1">
                    {filteredApplicationTypes.map(type => (
                      <label
                        key={type.APPLICATION_TYPE_ID}
                        className="flex items-center px-3 py-2 hover:bg-blue-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={(localFilters.application_types || []).includes(type.APPLICATION_TYPE_ID)}
                          onChange={() => handleApplicationTypeToggle(type.APPLICATION_TYPE_ID)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-3 text-sm text-gray-700">
                          {type.TYPE_NAME}
                          {type.DESCRIPTION && (
                            <span className="block text-xs text-gray-500 mt-0.5">{type.DESCRIPTION}</span>
                          )}
                        </span>
                      </label>
                    ))}
                    {filteredApplicationTypes.length === 0 && (
                      <div className="px-3 py-4 text-center text-sm text-gray-500">
                        No application types found
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons - fills 4th column in grid */}
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={handleReset}
              className="px-4 py-2 text-sm text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 hover:border-slate-400 transition-all shadow-sm hover:shadow flex items-center space-x-1.5 font-medium"
              disabled={isApplying}
            >
              <X className="h-4 w-4" />
              <span>Clear</span>
            </button>
            <button
              onClick={handleApply}
              disabled={isApplying}
              className={`px-4 py-2 text-sm text-white rounded-lg transition-all flex items-center space-x-1.5 font-medium shadow-sm hover:shadow ${
                isApplying 
                  ? 'bg-blue-400 cursor-not-allowed' 
                  : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700'
              }`}
            >
              {isApplying ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Applying...</span>
                </>
              ) : (
                <>
                  <Filter className="h-4 w-4" />
                  <span>Apply Filters</span>
                </>
              )}
            </button>
          </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedJobCardFilters;
